import { CdkDragDrop, moveItemInArray } from "@angular/cdk/drag-drop";
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { UiAlertComponent } from "@comunicare/comunicare-dashboard-modals";
import {
  CONNECTION_OPTION,
  IAccount,
  ICareplan,
  IGlobalParameter,
  IPreferenceSendAlert,
  ITileParameter,
  ITranslation,
  IWidgetParameter,
  IWidgetStored,
  PatientsGroup,
  PatientWidgetName,
  PreferenceContext,
  Reference,
  SEND_MAIL,
} from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, from, Observable, of, Subject } from "rxjs";
import { concatAll, first, map, startWith, switchMap, takeUntil, tap } from "rxjs/operators";
import { ErrorOnGettingCaremateIDComponent } from "src/app/components/error-on-getting-caremate-id/error-on-getting-caremate-id.component";
import { ResetOwnPasswordComponent } from "src/app/components/forms/reset-own-password/reset-own-password.component";
import { WidgetItem } from "src/app/components/widgets/widget-item";
import { RouteData } from "src/app/models/route-data.interface";
import { CareplansService } from "src/app/providers/careplans.service";
import { GroupService } from "src/app/providers/group-api.service";
import { LanguagesService } from "src/app/providers/languages.service";
import { PreferenceService } from "src/app/providers/preference.service";
import { SessionService } from "src/app/providers/session.service";
import { TileManager } from "src/app/providers/tile-manager.service";
import { UserService } from "src/app/providers/user.service";
import { WidgetManagerService } from "src/app/providers/widget-manager.service";
import { FormPage } from "../formPage";

@Component({
  selector: "app-preference-page",
  templateUrl: "./preference-page.component.html",
  styleUrls: ["./preference-page.component.scss"],
})
export class PreferencePageComponent extends FormPage implements OnInit, OnDestroy {
  public previousUrl: string;

  @ViewChild("careplanInput") careplanInput: ElementRef<HTMLInputElement>;
  @ViewChild("groupInput") groupInput: ElementRef<HTMLInputElement>;

  @ViewChild("helpAlert") helpAlert: UiAlertComponent;
  public pageLoaded = false;
  public errorMessage: string;
  public FRANCE: string;
  public country: string;
  public preferenceForm = this.fb.group({
    preferredLanguage: ["", Validators.required],
    defaultLandingPage: ["", Validators.required],
    preferredOrg: ["", Validators.required],
    preferredService: ["", Validators.required],
    usePreferredLanguageWhenConflict: [],
    keepLayoutFromPatientPage: [],
    showAllWidgetWhenDetails: [],
    nbColumn: ["", [Validators.required]],
    inactivityDuration: ["", Validators.required],
    go2PatientAfterInscription: [],
    sendMail: [SEND_MAIL.NONE],
    toggleAlertCtrl: [false],
  });
  public availableServices: Reference[];
  public availableOrgs: Reference[];
  private mapOrgsAndServices: Map<string, Reference[]> = new Map<string, Reference[]>();
  public availableWidgets: string[];
  public selectedWidgets: string[] = [];
  public availableAlertColors: string[] = ["1", "2", "3"];
  public selectedAlertColors: string[] = [];
  public widgetItems: WidgetItem[] = [];
  public selectedTile: PatientWidgetName[] = [];
  public hasError = false;
  public availableTile: string[] = this.initTileList();
  public saveInProgress = false;
  public showHelp = false;
  public availableLangs: ITranslation[];
  private account: IAccount;
  /** Subject that emits when the component has been destroyed. */
  private onDestroy$ = new Subject<void>();
  public SEND_MAIL = SEND_MAIL; // pour le template

  // Alerts //

  public alertPrefLoading = true;
  // for group
  public availablePatientGroups: PatientsGroup[] = [];
  public filteredGroups: Observable<PatientsGroup[]>;
  public groupCtrl = new FormControl();
  public selectedGroups: PatientsGroup[] = [];

  // for careplans
  public availableCareplans: ICareplan[] = [];
  public filteredCareplans: Observable<ICareplan[]>;
  public careplanCtrl = new FormControl();
  public selectedCareplans: ICareplan[] = [];

  constructor(
    private router: Router,
    private fb: UntypedFormBuilder,
    private preferenceService: PreferenceService,
    private translateService: TranslateService,
    public sessionService: SessionService,
    private userService: UserService,
    private widgetManagerService: WidgetManagerService,
    private snackBar: MatSnackBar,
    // public dialogRef: MatDialogRef<PreferenceDialogComponent>,
    private dialog: MatDialog,
    public tileManager: TileManager,
    protected languagesService: LanguagesService,
    private groupService: GroupService,
    private careplanService: CareplansService
  ) {
    super();

    this.router.events
      .pipe(
        first(),
        tap(() => {
          console.log(this.router.getCurrentNavigation());
        }),
        map(() => this.router.getCurrentNavigation().previousNavigation?.finalUrl?.toString())
      )
      .subscribe((previousUrl) => {
        this.previousUrl = previousUrl;
      });
    // All the widgets are listed (prescriptions included), then filterAuthorizedWidgetNames removes those whose route
    // is not authorized: the prescription widgets are in widgetAuthorizationMap, so their rights are checked there
    this.availableWidgets = this.widgetManagerService.filterAuthorizedWidgetNames(
      this.widgetManagerService.getAllVisibleWidgetName(true, true),
      (routeName) => this.userService.isAuthorizedSync(null, routeName, "GET")
    );
    this.account = this.sessionService.account;
    this.country = this.account.referenceCountry ? this.account.referenceCountry : this.sessionService.currentCountry;
    this.FRANCE = this.sessionService.FRANCE;
    const deactivatedAuth = this.account.authOptionsDeactivated ? this.account.authOptionsDeactivated : [];
    this.account.authOptionsDeactivated = deactivatedAuth;
    if (this.country === this.FRANCE && deactivatedAuth.includes(CONNECTION_OPTION.PSC)) {
      this.preferenceForm.addControl("togglePSC", new UntypedFormControl(false));
    } else if (this.country === this.FRANCE && !deactivatedAuth.includes(CONNECTION_OPTION.PSC)) {
      this.preferenceForm.addControl("togglePSC", new UntypedFormControl(true));
    } else if (this.country !== this.FRANCE && deactivatedAuth.includes(CONNECTION_OPTION.EHEALTH)) {
      this.preferenceForm.addControl("toggleEHealth", new UntypedFormControl(false));
    } else if (this.country !== this.FRANCE && !deactivatedAuth.includes(CONNECTION_OPTION.EHEALTH)) {
      this.preferenceForm.addControl("toggleEHealth", new UntypedFormControl(true));
    }
  }

  ngOnInit(): void {
    this.languagesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((languages) => (this.availableLangs = languages));

    forkJoin([this.loadGroups$(), this.loadCareplanTemplates$()])
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(([groups, careplans]) => {
        this.availablePatientGroups = groups;
        this.availableCareplans = careplans;
        this.loadEmailAlertPref();
      });

    // for group
    this.filteredGroups = this.groupCtrl.valueChanges.pipe(
      startWith(""),
      map((value) => this._filterGroups(value))
    );

    // for careplans
    this.filteredCareplans = this.careplanCtrl.valueChanges.pipe(
      startWith(""),
      map((name) => this._filterCareplans(name))
    );

    const $getTilePrefObs = this.preferenceService.list(PreferenceContext.TILE);
    const $getWidgetPrefObs = this.preferenceService.list(PreferenceContext.WIDGET);

    this.preferenceService
      .getGlobal()
      .pipe(first(), takeUntil(this.onDestroy$))
      .subscribe((parameters) => {
        let selectedOrg: Reference;
        let selectedService: Reference;
        if (parameters) {
          selectedOrg = parameters.preferredOrg;
          selectedService = parameters.preferredService;
          const layout = parameters.keepLayoutFromPatientPage !== undefined ? parameters.keepLayoutFromPatientPage : false;
          const selectedInactivityDuration = parameters?.inactifPatientDuration ? parameters.inactifPatientDuration : "1 week";
          this.preferenceForm
            .get("preferredLanguage")
            .setValue(parameters.preferredLanguage ? parameters.preferredLanguage : this.availableLangs[0]);
          this.preferenceForm
            .get("defaultLandingPage")
            .setValue(this.landingPages.find((lp) => lp === parameters.defaultLandingPage) ?? this.landingPages[0]);
          this.preferenceForm.get("usePreferredLanguageWhenConflict").setValue(parameters.usePreferredLanguageWhenConflict);
          this.preferenceForm.get("showAllWidgetWhenDetails").setValue(parameters.showAllWidgetWhenDetails);
          this.preferenceForm.get("keepLayoutFromPatientPage").setValue(layout);
          this.preferenceForm.get("inactivityDuration").setValue(selectedInactivityDuration);
          this.preferenceForm.get("go2PatientAfterInscription").setValue(parameters.go2PatientAfterInscription);
        } else {
          this.preferenceForm.get("defaultLandingPage").setValue(this.landingPages[0]);
          if (this.userService.isMonitoringUser) {
            selectedOrg = this.userService.allMonitoringOrganizations[0].asReference;
            selectedService = this.userService.allMonitoringServices.find(
              (s) => s.providedBy.reference === selectedOrg.reference
            )?.asReference;
            this.preferenceForm.get("preferredOrg").setValue(selectedOrg);
            this.preferenceForm.get("preferredService").setValue(this.userService.allMonitoringServices[0].asReference);
          } else {
            selectedOrg = this.userService.allOrganizations[0].asReference;
            selectedService = this.userService.allServices.find((s) => s.providedBy.reference === selectedOrg.reference)?.asReference;
            this.preferenceForm.get("preferredService").setValue(this.userService.allServices[0].asReference);
          }
          this.preferenceForm.get("usePreferredLanguageWhenConflict").setValue(true);
          this.preferenceForm.get("keepLayoutFromPatientPage").setValue(false);
          this.preferenceForm.get("showAllWidgetWhenDetails").setValue(false);
          this.preferenceForm.get("preferredLanguage").setValue(this.sessionService.userLang);
          this.preferenceForm.get("inactivityDuration").setValue("1 week");
          this.preferenceForm.get("go2PatientAfterInscription").setValue(undefined);
        }
        this.preferenceForm.get("preferredOrg").setValue(selectedOrg);
        this.preferenceForm.get("preferredService").setValue(selectedService);
        forkJoin([$getWidgetPrefObs, $getTilePrefObs])
          .pipe(takeUntil(this.onDestroy$))
          .subscribe(([a, b]: Array<IWidgetParameter | ITileParameter | IPreferenceSendAlert>) => {
            const widgetParameters: IWidgetParameter = a as IWidgetParameter;
            const tileParameters: ITileParameter = b as ITileParameter;

            if (widgetParameters) {
              if (widgetParameters.nbColumn) {
                this.preferenceForm.get("nbColumn").setValue(widgetParameters.nbColumn);
              }
              if (widgetParameters.widgets) {
                this.widgetItems = this.widgetManagerService.getWidgetItems(widgetParameters.widgets);
                this.selectedWidgets = this.widgetItems.map((v) => v.name);
              }
            } else {
              this.preferenceForm.get("nbColumn").setValue(this.widgetManagerService.defaultParameter.nbColumn);
              this.selectedWidgets = this.widgetManagerService.defaultParameter.widgets.map((v) => v.name);
            }

            if (tileParameters) {
              if (tileParameters.list) {
                this.selectedTile = tileParameters.list;
                // reorder the availableTile with the db tiles order
                this.selectedTile.forEach((dbTile, i) => {
                  const indexFound = this.availableTile.findIndex((tile) => tile === dbTile);
                  moveItemInArray(this.availableTile, indexFound, i);
                });
              }
            } else {
              this.selectedTile = [...this.availableTile] as PatientWidgetName[];
            }

            if (this.userService.isMonitoringUser) {
              this.availableOrgs = this.userService.allMonitoringOrganizations.map((s) => s.asReference);
              this.availableServices = this.userService.allMonitoringServices.map((s) => s.asReference);
            } else {
              this.availableOrgs = this.userService.allOrganizations.map((s) => s.asReference);
              this.availableServices = this.userService.allServices.map((s) => s.asReference);
            }
            for (const org of this.availableOrgs) {
              const services = this.userService.isMonitoringUser ? this.userService.allMonitoringServices : this.userService.allServices;
              const orgServices = services.filter((s) => s.providedBy.reference === org.reference).map((s) => s.asReference);
              this.mapOrgsAndServices.set(org.reference, orgServices);
            }
            if (!selectedOrg) {
              selectedOrg = this.availableOrgs[0];
              this.preferenceForm.get("preferredOrg").setValue(selectedOrg);
            }
            this.availableServices = this.mapOrgsAndServices.get(selectedOrg.reference);
            if (!selectedService) {
              selectedService = this.availableServices[0];
              this.preferenceForm.get("preferredService").setValue(selectedService);
            }
            this.setupMonitOrgWatch();
            this.pageLoaded = true;
          });
      });
  }

  public loadEmailAlertPref(): void {
    this.preferenceService.list(PreferenceContext.EMAIL_ALERTS).subscribe((emailAlertParameters) => {
      if (emailAlertParameters) {
        this.preferenceForm.get("toggleAlertCtrl").setValue(+emailAlertParameters.sendMail !== 0);
        this.preferenceForm.get("sendMail").setValue(emailAlertParameters.sendMail);
        this.selectedAlertColors = emailAlertParameters.alertLevels.map((level) => level.toString());
        this.selectedCareplans = this.availableCareplans.filter((careplan) =>
          emailAlertParameters.careplanIds.includes(careplan.support[0].reference)
        );
        this.selectedGroups = this.availablePatientGroups.filter((group) => emailAlertParameters.patientGroups.includes(group.groupName));
      }
    });
    this.setupToggleAlertCtrl();
    this.alertPrefLoading = false;
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  // for careplans

  public loadCareplanTemplates$(): Observable<ICareplan[]> {
    return this.careplanService
      .getCareplanTemplates(undefined, this.sessionService.userLang)
      .pipe(tap((careplans) => (this.availableCareplans = careplans)));
  }

  private _filterCareplans(value: string): ICareplan[] {
    const filterValue = value?.toLowerCase?.() || "";
    const filtered = this.availableCareplans.filter(
      (careplan) =>
        careplan.description.toLowerCase().includes(filterValue) &&
        !this.selectedCareplans.some((sel) => sel.support[0].reference === careplan.support[0].reference) // Don't show already selected careplans
    );

    return filterValue
      ? filtered
      : ([{ description: this.translateService.instant("page.preference.all") }, ...filtered] as unknown as ICareplan[]);
  }

  selectCareplan(careplan: ICareplan): void {
    if (careplan.description === this.translateService.instant("page.preference.all")) {
      this.selectedCareplans = [...this.availableCareplans];
    } else {
      this.selectedCareplans.push(careplan);
    }
    this.careplanCtrl.setValue("");
    this.careplanInput.nativeElement.value = "";
  }

  removeCareplan(careplan: ICareplan): void {
    this.selectedCareplans = this.selectedCareplans.filter((c) => c.description !== careplan.description);
  }

  // for groups

  public loadGroups$(): Observable<PatientsGroup[]> {
    if (!this.userService.isAuthorizedSync(null, "groups", "GET")) {
      return of([]);
    }
    return this.groupService.listAll().pipe(tap((groups) => (this.availablePatientGroups = groups)));
  }

  private _filterGroups(value: string): PatientsGroup[] {
    const filterValue = value?.toLowerCase?.() || "";
    const filtered = this.availablePatientGroups.filter(
      (group) =>
        group.groupName.toLowerCase().includes(filterValue) &&
        // Don't show already selected groups
        !this.selectedGroups.some((sel) => sel._id === group._id)
    );
    return filterValue
      ? filtered
      : ([{ groupName: this.translateService.instant("page.preference.all") }, ...filtered] as unknown as PatientsGroup[]);
  }

  selectGroup(group: PatientsGroup): void {
    if (group.groupName === this.translateService.instant("page.preference.all")) {
      this.selectedGroups = [...this.availablePatientGroups];
    } else {
      this.selectedGroups.push(group);
    }
    this.groupCtrl.setValue(null);
    this.groupInput.nativeElement.value = "";
  }

  removeGroup(group: PatientsGroup): void {
    this.selectedGroups = this.selectedGroups.filter((g) => g._id !== group._id);
  }

  private setupMonitOrgWatch() {
    this.preferenceForm
      .get("preferredOrg")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((org: Reference) => {
        this.availableServices = this.mapOrgsAndServices.get(org.reference);
        this.preferenceForm.get("preferredService").setValue(this.availableServices[0]);
      });
  }

  private setupToggleAlertCtrl() {
    this.preferenceForm
      .get("toggleAlertCtrl")
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((value) => {
        if (value && !this.preferenceForm.get("sendMail").value) {
          this.preferenceForm.get("sendMail").setValue(SEND_MAIL.DAILY_SUMMARY, { emitEvent: false });
        }
      });
  }

  public initTileList(): string[] {
    return Object.keys(PatientWidgetName)
      .map((k) => PatientWidgetName[k])
      .filter((key) => {
        const perm = this.getPermissionsKeyFromName(key);
        return this.userService.isAuthorizedSync(null, perm.routeName, perm.method);
      });
  }

  private getPermissionsKeyFromName(key: string): {
    routeName: string;
    method: string;
  } {
    switch (key) {
      case PatientWidgetName.OBSERVATIONS:
        return {
          routeName: "observations",
          method: "GET",
        };
      case PatientWidgetName.DRUGS:
        return {
          routeName: "entitylinks",
          method: "GET",
        };
      case PatientWidgetName.QUESTIONNAIRES:
        return {
          routeName: "dashboard/questionnaires",
          method: "GET",
        };
      case PatientWidgetName.TELECONSULTATIONS:
        return {
          routeName: "appointmentsPractitioner",
          method: "GET",
        };
      case PatientWidgetName.ALERTS:
        return {
          routeName: "rulealerts",
          method: "GET",
        };
      case PatientWidgetName.VITAL_SIGNS:
        return {
          routeName: "dashboard/vitalProfileDefinitions",
          method: "GET",
        };
      case PatientWidgetName.CAREPLANS:
        return {
          routeName: "careplans",
          method: "GET",
        };
      case PatientWidgetName.COMMUNICATIONS:
        return {
          routeName: "dashboard/communications",
          method: "GET",
        };
      case PatientWidgetName.RA:
        return {
          routeName: "riskAssessments",
          method: "GET",
        };
      case PatientWidgetName.REWARDS:
        return {
          routeName: "rewardScoresByPatient",
          method: "GET",
        };
      case PatientWidgetName.DOCUMENTS:
        return {
          routeName: "dashboard/documentsInfos",
          method: "GET",
        };
      case PatientWidgetName.PRESCRIPTIONS:
        return {
          routeName: "dashboard/prescriptionInfos",
          method: "GET",
        };
    }
  }

  public getForm(): UntypedFormGroup {
    return this.preferenceForm;
  }

  public save(): void {
    if (this.preferenceForm.valid) {
      this.saveInProgress = true;
      const widgetsToStore = this.selectedWidgets.map((widgetName: string) => {
        const widgetItem = this.widgetItems.find((w) => w.name === widgetName);
        return {
          name: widgetName,
          row: widgetItem ? widgetItem.row : 0,
          column: widgetItem ? widgetItem.column : 0,
        } as IWidgetStored;
      });
      const widgetParameter: IWidgetParameter = {
        nbColumn: this.preferenceForm.get("nbColumn").value,
        widgets: widgetsToStore,
      };
      this.orderTile();
      const tileParameter: ITileParameter = {
        list: this.selectedTile,
      };

      const emailAlertsParameter: IPreferenceSendAlert = {
        /*
        The sendMail field of the preference is:
        - 0 (=SEND_MAIL.NONE) if toggle is disabled
        - 1, 2 or 3: if toggle is enabled and a mail frequency type is selected
        */
        sendMail: !this.preferenceForm.get("toggleAlertCtrl").value
          ? SEND_MAIL.NONE
          : this.preferenceForm.get("sendMail").value
          ? this.preferenceForm.get("sendMail").value
          : SEND_MAIL.NONE,
        alertLevels: this.selectedAlertColors.map((color) => parseInt(color)),
        careplanIds: this.selectedCareplans.map((careplan) => careplan.support[0].reference),
        patientGroups: this.selectedGroups.map((group) => group.groupName),
      };

      const globalParameter: IGlobalParameter = {
        preferredLanguage: this.preferenceForm.get("preferredLanguage").value,
        defaultLandingPage: this.preferenceForm.get("defaultLandingPage").value,
        usePreferredLanguageWhenConflict: this.preferenceForm.get("usePreferredLanguageWhenConflict").value,
        preferredService: this.preferenceForm.get("preferredService").value,
        preferredOrg: this.preferenceForm.get("preferredOrg").value,
        keepLayoutFromPatientPage: this.preferenceForm.get("keepLayoutFromPatientPage").value,
        showAllWidgetWhenDetails: this.preferenceForm.get("showAllWidgetWhenDetails").value,
        inactifPatientDuration: this.preferenceForm.get("inactivityDuration").value,
        go2PatientAfterInscription: this.preferenceForm.get("go2PatientAfterInscription").value,
      };
      TileManager.showAll = globalParameter.showAllWidgetWhenDetails;
      const globalObservable = this.preferenceService.update({
        context: PreferenceContext.GLOBAL,
        parameters: globalParameter,
      });
      const widgetObservable = this.preferenceService.update({
        context: PreferenceContext.WIDGET,
        parameters: widgetParameter,
      });
      const tileObservable = this.preferenceService.update({
        context: PreferenceContext.TILE,
        parameters: tileParameter,
      });

      const emailAlertsObservable = this.preferenceService.update({
        context: PreferenceContext.EMAIL_ALERTS,
        parameters: emailAlertsParameter,
      });

      if (this.country === this.FRANCE && this.preferenceForm.get("togglePSC").value) {
        this.account.authOptionsDeactivated = this.account.authOptionsDeactivated.filter((a) => a !== CONNECTION_OPTION.PSC);
      } else if (
        this.country === this.FRANCE &&
        !this.preferenceForm.get("togglePSC").value &&
        !this.account.authOptionsDeactivated.includes(CONNECTION_OPTION.PSC)
      ) {
        this.account.authOptionsDeactivated.push(CONNECTION_OPTION.PSC);
      } else if (this.country !== this.FRANCE && this.preferenceForm.get("toggleEHealth").value) {
        this.account.authOptionsDeactivated = this.account.authOptionsDeactivated.filter((a) => a !== CONNECTION_OPTION.EHEALTH);
      } else if (
        this.country !== this.FRANCE &&
        !this.preferenceForm.get("toggleEHealth").value &&
        !this.account.authOptionsDeactivated.includes(CONNECTION_OPTION.EHEALTH)
      ) {
        this.account.authOptionsDeactivated.push(CONNECTION_OPTION.EHEALTH);
      }

      from([widgetObservable, tileObservable, globalObservable, emailAlertsObservable])
        .pipe(
          concatAll(),
          // Use concatAll instead of forkJoin because [widgetObservable, tileObservable, globalObservable, emailAlertsObservable] modify the same resource.
          // Executing them in parallel (with forkJoin) could result in race conditions and lost updates.
          // concatAll ensures they are processed one after another, preserving data integrity.
          switchMap(() => this.userService.updateAccount(this.account)),
          switchMap(() => {
            const lang = this.preferenceForm.value.preferredLanguage;
            this.translateService.use(lang);
            this.sessionService.userLang = lang;
            return this.translateService.get("common.saveSuccess");
          }) //Do not put ngOnDestroy here because we must be sure that all requests have been processed, otherwise we will lose some preference changes made by the doctor.
        )
        .subscribe((trans) => {
          this.snackBar.open(trans, "Ok", { duration: 3000 });
          this.back();
        });
    } else {
      this.showError();
    }
  }

  private showError(): void {
    this.hasError = true;
    this.translateService
      .get("error.invalidData")
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((trans) => {
        this.snackBar.open(trans, "Ok", {
          duration: 2000,
        });
      });
  }

  public get landingPages(): string[] {
    return this.router.config
      .filter(
        (route) => route.data && (route.data as RouteData).canBeLanding && this.userService.isAuthorizedSync(null, "/" + route.path, "GET")
      )
      .map((route) => route.path);
  }

  public compareReference(o1: Reference, o2: Reference): boolean {
    return o1.reference === o2.reference;
  }

  public isEnabledWidget(widgetName: string): boolean {
    if (this.selectedWidgets) {
      return this.selectedWidgets.includes(widgetName);
    }
    return false;
  }

  public isEnabledAlert(alertColor: string): boolean {
    if (this.selectedAlertColors) {
      return this.selectedAlertColors.includes(alertColor);
    }
    return false;
  }

  public isTileEnabled(tileName: PatientWidgetName): boolean {
    return this.selectedTile.findIndex((t) => t === tileName) > -1;
  }

  public changeTile(enabled: boolean, tileName: PatientWidgetName): void {
    if (enabled) {
      this.selectedTile.push(tileName);
    } else {
      const posIndex = this.selectedTile.findIndex((w) => w === tileName);
      if (posIndex >= 0) {
        this.selectedTile.splice(posIndex, 1);
      }
    }
  }

  public changeWidget(enabled: boolean, widgetName: string): void {
    if (enabled) {
      this.selectedWidgets.push(widgetName);
    } else {
      const posIndex = this.selectedWidgets.findIndex((w) => w === widgetName);
      if (posIndex >= 0) {
        this.selectedWidgets.splice(posIndex, 1);
      }
    }
  }

  public changeAlertColor(enabled: boolean, color: string): void {
    if (enabled) {
      this.selectedAlertColors.push(color);
    } else {
      const posIndex = this.selectedAlertColors.findIndex((w) => w === color);
      if (posIndex >= 0) {
        this.selectedAlertColors.splice(posIndex, 1);
      }
    }
  }

  public tileDropped(event: CdkDragDrop<string[]>): void {
    moveItemInArray(this.availableTile, event.previousIndex, event.currentIndex);
  }

  public handleTileArrowClicked(isUp: boolean, index: number): void {
    moveItemInArray(this.availableTile, index, isUp ? index - 1 : index + 1);
  }

  private orderTile() {
    const selectedTileOrdered: PatientWidgetName[] = [];
    this.availableTile.forEach((tile) => {
      if (this.isTileEnabled(tile as PatientWidgetName)) {
        selectedTileOrdered.push(tile as PatientWidgetName);
      }
    });
    this.selectedTile = selectedTileOrdered;
  }

  public onShowHelp(): void {
    this.showHelp = true;
  }

  public dismissHelp(): void {
    this.helpAlert.onDismiss();
  }

  public displayConfirmResetModal(): void {
    const account = this.sessionService.account;
    if (!account.caremateIdentifier) {
      this.dialog.open(ErrorOnGettingCaremateIDComponent, {
        width: "450px",
        height: "200px",
        data: {
          currentUser: {
            mail: account.mail,
            name: account.name,
            firstname: account.firstname,
          },
          preference: true,
        },
      });
      return;
    }
    this.dialog.open(ResetOwnPasswordComponent, {
      data: {
        login: account.mail,
        lang: this.sessionService.userLang,
      },
    });
  }

  public back(): void {
    this.router.navigateByUrl(this.previousUrl ?? "/");
  }
}
